/** @jsxImportSource @emotion/react */
import {
  presentBathCount,
  presentUser,
  propertyIdPresenter,
} from '@kaiizen/kai-presenters';
import { ApiPropertyProject, isProjectEditable } from '@kaiizen/kai-pro-api';
import {
  usePropertyHeroForPropertyProject,
  usePropertyHeroForScan,
  useUsers,
  useUsersForScan,
} from '@kaiizen/kai-pro-api-react-state';
import { useEffect, useRef, useState } from 'react';

import { getScanStatusText } from '../../Partials/ScanStatusPill';
import { StatusColor } from '../../Partials/StatusPill';
import { useProjectTypes } from '../../State';
import {
  formatDashboardDate,
  getArtifactPresentation,
  isProjectArchived,
  projectNeedsAttention,
} from './dashboardHelpers';
import {
  ADDRESS_DOT_STYLE,
  ADDRESS_TEXT_STYLE,
  AMOUNT_STYLE,
  ARTIFACT_TAG_STYLE,
  ASSIGNEE_TOOLTIP_CARET_STYLE,
  ASSIGNEE_TOOLTIP_STYLE,
  ASSIGNEE_TOOLTIP_TRIGGER_STYLE,
  ASSIGNEE_VALUE_STYLE,
  PROJECT_ACTION_MENU_BUTTON_STYLE,
  PROJECT_ACTION_MENU_ITEM_STYLE,
  PROJECT_ACTION_MENU_PANEL_STYLE,
  PROJECT_ACTION_MENU_STYLE,
  PROJECT_ADDRESS_MUTED_STYLE,
  PROJECT_ADDRESS_STYLE,
  PROJECT_CARD_CLOSED_STYLE,
  PROJECT_CARD_STYLE,
  PROJECT_DETAILS_STYLE,
  PROJECT_DUE_ALERT_DOT_STYLE,
  PROJECT_FACTS_STYLE,
  PROJECT_IMAGE_STYLE,
  PROJECT_MAIN_STYLE,
  PROJECT_META_LABEL_STYLE,
  PROJECT_META_ROW_STYLE,
  PROJECT_META_STYLE,
  PROJECT_META_VALUE_DUE_ALERT_ROW_STYLE,
  PROJECT_META_VALUE_DUE_ALERT_STYLE,
  PROJECT_META_VALUE_MUTED_STYLE,
  PROJECT_META_VALUE_STRONG_STYLE,
  PROJECT_META_VALUE_STYLE,
  PROJECT_NAME_STYLE,
  STATE_TAG_MUTED_STYLE,
  STATUS_TAG_STYLE,
  TAG_ROW_STYLE,
  TYPE_TAG_STYLE,
} from './dashboardStyles';

export const DashboardProjectRow = ({
  project,
  marketName,
  propertyIdLabel,
  onArchive,
  onUnarchive,
  onReactivate,
  actionInFlight = false,
  onClick,
}: {
  project: ApiPropertyProject;
  marketName?: string;
  propertyIdLabel: string;
  onArchive?: (project: ApiPropertyProject) => void;
  onUnarchive?: (project: ApiPropertyProject) => void;
  onReactivate?: (project: ApiPropertyProject) => void;
  actionInFlight?: boolean;
  onClick: () => void;
}) => {
  const [isActionMenuOpen, setIsActionMenuOpen] = useState(false);
  const actionMenuRef = useRef<HTMLDivElement>(null);
  // Pass the records themselves, not their ids: the list query already returned
  // latestScan, orgProperty and the project, so handing those over preloads the
  // caches instead of re-fetching each one per row.
  const scanHeroUri = usePropertyHeroForScan(project.latestScan, project);
  const projectHeroUri = usePropertyHeroForPropertyProject(
    project.latestScan ? undefined : project,
  );
  const { users } = useUsers();
  const { usersForScan } = useUsersForScan(project.firstScan?.id);
  const { getProjectTypeLabel } = useProjectTypes();
  const heroUri = scanHeroUri ?? projectHeroUri;
  const property = project.orgProperty;
  const facts = property?.customerMetadata;
  const artifact = getArtifactPresentation(project);
  const dueDate = project.latestScan?.customerMetadata?.dueDate
    ?? project.firstScan?.customerMetadata?.dueDate;
  const needsAttention = projectNeedsAttention(project);
  const budget = project.customerMetadata?.budget;
  const projectTypeLabel = project.customerMetadata?.projectType
    ? getProjectTypeLabel(project.customerMetadata.projectType)?.toUpperCase()
    : undefined;
  const bathCount = presentBathCount(
    facts?.fullBathCount,
    facts?.halfBathCount,
    facts?.bathroomCount,
  );
  const isArchivedView = isProjectArchived(project);
  const isClosedView = !isArchivedView && project.status === 'completed';
  const isNonActiveView = isClosedView || isArchivedView;
  const rowDate = isArchivedView
    ? formatDashboardDate(project.archivedAt)
    : formatDashboardDate(project.updatedAt);
  const rowDateLabel = isArchivedView ? 'Archived' : 'Completed';
  const stateLabel = isArchivedView ? 'Archived' : 'Completed';
  const statusScan = (isProjectEditable(project)
    ? project.latestScan
    : project.latestApprovedScan) ?? project.latestScan;
  const artifactColor: StatusColor = artifact.label === 'Change Order'
    ? 'darkOrange'
    : artifact.label === 'Punch List'
    ? 'darkBlue'
    : artifact.label === 'Close Out'
    ? 'darkGreen'
    : 'charcoal';
  const lifecycle = statusScan?.scanLifecycleStatus;
  const statusText = getScanStatusText({
    scanType: statusScan?.scanType,
    status: lifecycle,
  });
  const showsApprovedStatus = lifecycle === 'approved' || statusScan?.scanType === 'progress';
  const amountLabel = typeof budget === 'number'
    ? `$${budget.toLocaleString(undefined, { maximumFractionDigits: 0 })}`
    : '--';
  const presentedUserNamesById = new Map(
    (users ?? []).map(user => [user.id, presentUser(user)]),
  );
  const projectManagerNames = (project.managerIds ?? [])
    .map(userId => presentedUserNamesById.get(userId))
    .filter((name): name is string => Boolean(name));
  const fieldAgentNames = (usersForScan ?? [])
    .map(user => user.name?.trim())
    .filter((name): name is string => Boolean(name));
  const assigneeCount = new Set([
    ...(project.managerIds ?? []),
    ...(usersForScan ?? []).map(user => user.id),
  ]).size;
  const assigneeLabel = assigneeCount > 0
    ? `${assigneeCount} ${assigneeCount === 1 ? 'Assignee' : 'Assignees'}`
    : '--';
  const hasAssigneeTooltip = projectManagerNames.length > 0 || fieldAgentNames.length > 0;
  const hasReviewAttention = lifecycle === 'pendingReview' || lifecycle === 'inReview';
  const hasDueDateAttention = needsAttention
    && !hasReviewAttention
    && lifecycle !== 'correctionsRequested';
  const statusTagColor = lifecycle === 'pendingReview'
      || lifecycle === 'inReview'
      || lifecycle === 'correctionsRequested'
    ? '#b82c2c'
    : showsApprovedStatus
    ? '#4d8f5e'
    : artifact.label === 'Close Out' && !showsApprovedStatus
    ? '#5a5a5a'
    : artifactColor === 'darkGreen'
    ? '#4d8f5e'
    : artifactColor === 'darkOrange'
    ? '#cc7a2b'
    : artifactColor === 'darkBlue'
    ? '#2878b8'
    : '#5a5a5a';
  const primaryAddressStyle = isArchivedView
    ? PROJECT_ADDRESS_MUTED_STYLE
    : PROJECT_ADDRESS_STYLE;
  const marketAndIdValueStyle = isArchivedView
    ? PROJECT_META_VALUE_MUTED_STYLE
    : PROJECT_META_VALUE_STRONG_STYLE;

  useEffect(() => {
    if (!isActionMenuOpen) return;

    const onMouseDown = (event: MouseEvent) => {
      if (!actionMenuRef.current?.contains(event.target as Node)) {
        setIsActionMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [isActionMenuOpen]);

  return (
    <div
      css={isNonActiveView ? PROJECT_CARD_CLOSED_STYLE : PROJECT_CARD_STYLE}
      onClick={onClick}
    >
      <div css={PROJECT_MAIN_STYLE}>
        {heroUri
          ? (
            <img
              src={heroUri}
              alt={property?.address ?? ''}
              css={PROJECT_IMAGE_STYLE}
            />
          )
          : <div css={PROJECT_IMAGE_STYLE} />}
        <div css={PROJECT_DETAILS_STYLE}>
          <div css={TAG_ROW_STYLE}>
            {projectTypeLabel && <span css={TYPE_TAG_STYLE}>{projectTypeLabel}</span>}
            {isArchivedView ? <span css={STATE_TAG_MUTED_STYLE}>{stateLabel}</span> : isClosedView
              ? (
                <>
                  <span css={ARTIFACT_TAG_STYLE}>Project</span>
                  <span css={STATE_TAG_MUTED_STYLE}>{stateLabel}</span>
                </>
              )
              : (
                <>
                  <span css={ARTIFACT_TAG_STYLE}>{artifact.label}</span>
                  <span css={[STATUS_TAG_STYLE, { color: statusTagColor }]}>
                    {hasReviewAttention ? `• ${statusText}` : statusText}
                  </span>
                </>
              )}
          </div>
          <div css={primaryAddressStyle}>
            {!isNonActiveView && <span css={ADDRESS_DOT_STYLE} />}
            <span css={ADDRESS_TEXT_STYLE}>
              {property?.address ?? 'Address not available'}
            </span>
            <span css={PROJECT_FACTS_STYLE}>
              {`${facts?.bedroomCount ?? '--'} bed | ${bathCount} bath | ${facts?.squareFeet ? `${facts.squareFeet.toLocaleString()} sqft` : '--'}`}
            </span>
          </div>
          <div css={PROJECT_META_ROW_STYLE}>
            <div css={PROJECT_META_STYLE}>
              <div css={PROJECT_META_LABEL_STYLE}>Market</div>
              <div css={marketAndIdValueStyle}>{marketName ?? '--'}</div>
            </div>
            <div css={PROJECT_META_STYLE}>
              <div css={PROJECT_META_LABEL_STYLE}>{propertyIdLabel}</div>
              <div css={marketAndIdValueStyle}>
                {propertyIdPresenter(property)}
              </div>
            </div>
            {isArchivedView
              ? (
                <>
                  <div css={PROJECT_META_STYLE}>
                    <div css={PROJECT_META_LABEL_STYLE}>{rowDateLabel}</div>
                    <div css={PROJECT_META_VALUE_MUTED_STYLE}>{rowDate}</div>
                  </div>
                  <div css={PROJECT_META_STYLE}>
                    <div css={PROJECT_META_LABEL_STYLE}>Amount</div>
                    <div css={[PROJECT_META_VALUE_MUTED_STYLE, AMOUNT_STYLE]}>
                      {amountLabel}
                    </div>
                  </div>
                </>
              )
              : isClosedView
              ? (
                <>
                  <div css={PROJECT_META_STYLE}>
                    <div css={PROJECT_META_LABEL_STYLE}>{rowDateLabel}</div>
                    <div css={PROJECT_META_VALUE_MUTED_STYLE}>{rowDate}</div>
                  </div>
                  <div css={PROJECT_META_STYLE}>
                    <div css={PROJECT_META_LABEL_STYLE}>Last Updated</div>
                    <div css={PROJECT_META_VALUE_MUTED_STYLE}>
                      {formatDashboardDate(project.updatedAt)}
                    </div>
                  </div>
                  <div css={PROJECT_META_STYLE}>
                    <div css={PROJECT_META_LABEL_STYLE}>Amount</div>
                    <div css={[PROJECT_META_VALUE_MUTED_STYLE, AMOUNT_STYLE]}>
                      {amountLabel}
                    </div>
                  </div>
                  <div css={PROJECT_META_STYLE}>
                    <div css={PROJECT_META_LABEL_STYLE}>Assignees</div>
                    <div css={PROJECT_META_VALUE_MUTED_STYLE}>
                      {assigneeLabel}
                    </div>
                  </div>
                  <div css={PROJECT_META_STYLE}>
                    <div css={PROJECT_META_LABEL_STYLE}>Project Name</div>
                    <div css={PROJECT_META_VALUE_MUTED_STYLE}>
                      {project.name || '--'}
                    </div>
                  </div>
                </>
              )
              : (
                <>
                  <div css={PROJECT_META_STYLE}>
                    <div css={PROJECT_META_LABEL_STYLE}>Due Date</div>
                    {hasDueDateAttention
                      ? (
                        <div css={PROJECT_META_VALUE_DUE_ALERT_ROW_STYLE}>
                          <span css={PROJECT_DUE_ALERT_DOT_STYLE} />
                          <div css={PROJECT_META_VALUE_DUE_ALERT_STYLE}>
                            {formatDashboardDate(dueDate)}
                          </div>
                        </div>
                      )
                      : (
                        <div css={PROJECT_META_VALUE_STYLE}>
                          {formatDashboardDate(dueDate)}
                        </div>
                      )}
                  </div>
                  <div css={PROJECT_META_STYLE}>
                    <div css={PROJECT_META_LABEL_STYLE}>Last Updated</div>
                    <div css={PROJECT_META_VALUE_STYLE}>
                      {formatDashboardDate(project.updatedAt)}
                    </div>
                  </div>
                  <div css={PROJECT_META_STYLE}>
                    <div css={PROJECT_META_LABEL_STYLE}>Amount</div>
                    <div css={[PROJECT_META_VALUE_STYLE, AMOUNT_STYLE]}>
                      {amountLabel}
                    </div>
                  </div>
                  <div css={PROJECT_META_STYLE}>
                    <div css={PROJECT_META_LABEL_STYLE}>Assignees</div>
                    <div css={ASSIGNEE_VALUE_STYLE}>
                      {hasAssigneeTooltip
                        ? (
                          <div css={ASSIGNEE_TOOLTIP_TRIGGER_STYLE}>
                            <span>{assigneeLabel}</span>
                            <div css={ASSIGNEE_TOOLTIP_STYLE}>
                              <div css={ASSIGNEE_TOOLTIP_CARET_STYLE} />
                              {projectManagerNames.length > 0
                                && projectManagerNames.map(name => (
                                  <div key={`project-manager-${name}`}>
                                    {`Project Manager: ${name}`}
                                  </div>
                                ))}
                              {fieldAgentNames.length > 0
                                && fieldAgentNames.map(name => (
                                  <div key={`field-agent-${name}`}>
                                    {`Field Agent: ${name}`}
                                  </div>
                                ))}
                            </div>
                          </div>
                        )
                        : assigneeLabel}
                    </div>
                  </div>
                  <div css={PROJECT_META_STYLE}>
                    <div css={PROJECT_META_LABEL_STYLE}>Project Name</div>
                    <div css={PROJECT_NAME_STYLE}>{project.name || '--'}</div>
                  </div>
                </>
              )}
          </div>
        </div>
      </div>
      {isNonActiveView
        ? (
          <>
            <div
              css={PROJECT_ACTION_MENU_STYLE}
              ref={actionMenuRef}
              onClick={event => event.stopPropagation()}
            >
              <button
                type='button'
                css={PROJECT_ACTION_MENU_BUTTON_STYLE}
                aria-label='Open project actions'
                onClick={() => setIsActionMenuOpen(open => !open)}
              >
                <svg width='20' height='20' viewBox='0 0 20 20' fill='none'>
                  <circle cx='10' cy='4' r='1.4' fill='currentColor' />
                  <circle cx='10' cy='10' r='1.4' fill='currentColor' />
                  <circle cx='10' cy='16' r='1.4' fill='currentColor' />
                </svg>
              </button>
              {isActionMenuOpen && (
                <div css={PROJECT_ACTION_MENU_PANEL_STYLE}>
                  {isClosedView
                    ? (
                      <>
                        <button
                          type='button'
                          css={PROJECT_ACTION_MENU_ITEM_STYLE}
                          disabled={actionInFlight}
                          onClick={() => {
                            setIsActionMenuOpen(false);
                            onReactivate?.(project);
                          }}
                        >
                          Reactivate Project
                        </button>
                        <button
                          type='button'
                          css={PROJECT_ACTION_MENU_ITEM_STYLE}
                          disabled={actionInFlight}
                          onClick={() => {
                            setIsActionMenuOpen(false);
                            onArchive?.(project);
                          }}
                        >
                          Archive Project
                        </button>
                      </>
                    )
                    : (
                      <button
                        type='button'
                        css={PROJECT_ACTION_MENU_ITEM_STYLE}
                        disabled={actionInFlight}
                        onClick={() => {
                          setIsActionMenuOpen(false);
                          onUnarchive?.(project);
                        }}
                      >
                        Unarchive Project
                      </button>
                    )}
                </div>
              )}
            </div>
          </>
        )
        : (
          <div
            css={PROJECT_ACTION_MENU_STYLE}
            ref={actionMenuRef}
            onClick={event => event.stopPropagation()}
          >
            <button
              type='button'
              css={PROJECT_ACTION_MENU_BUTTON_STYLE}
              aria-label='Open project actions'
              onClick={() => setIsActionMenuOpen(open => !open)}
            >
              <svg width='20' height='20' viewBox='0 0 20 20' fill='none'>
                <circle cx='10' cy='4' r='1.4' fill='currentColor' />
                <circle cx='10' cy='10' r='1.4' fill='currentColor' />
                <circle cx='10' cy='16' r='1.4' fill='currentColor' />
              </svg>
            </button>
            {isActionMenuOpen && onArchive && (
              <div css={PROJECT_ACTION_MENU_PANEL_STYLE}>
                <button
                  type='button'
                  css={PROJECT_ACTION_MENU_ITEM_STYLE}
                  disabled={actionInFlight}
                  onClick={() => {
                    setIsActionMenuOpen(false);
                    onArchive(project);
                  }}
                >
                  Archive Project
                </button>
              </div>
            )}
          </div>
        )}
    </div>
  );
};
